import { Callable } from '@prophecy/interfaces/generic';
import React from 'react';

import { KeyCodes } from './keyboard';

export function htmlToDOMElement(html: string): HTMLElement {
  var template = document.createElement('template');
  html = html.trim();
  template.innerHTML = html;
  return template.content.firstChild as HTMLElement;
}

export function htmlToSVGDOMElement(html: string): HTMLElement {
  var template = document.createElement('template');

  html = html.trim();
  template.innerHTML = `<svg>${html}</svg>`;
  return template.content.firstChild?.firstChild as HTMLElement;
}

export function getTranslateXY(element: HTMLElement): { x: number; y: number } {
  const style = window.getComputedStyle(element);
  const matrix = new DOMMatrixReadOnly(style.transform);
  return {
    x: matrix.m41,
    y: matrix.m42
  };
}

export function waitForElement(selector: string, root: HTMLElement, timeout?: number): Promise<Element> {
  return new Promise((resolve, reject) => {
    let start = performance.now();

    function checkForElement() {
      const element = root.querySelector(selector) as HTMLElement;
      if (element) {
        resolve(element);
      } else if (typeof timeout === 'number' && performance.now() - start > timeout) {
        reject(new Error(`Element with selector ${selector} was not found in the DOM after ${timeout}ms`));
      } else {
        requestAnimationFrame(checkForElement);
      }
    }
    checkForElement();
  });
}

export const preventDefault = (e: React.MouseEvent | React.KeyboardEvent | React.SyntheticEvent | Event) => {
  e.preventDefault();
};

export const stopPropagation = (e: React.MouseEvent | React.KeyboardEvent | React.SyntheticEvent | Event) => {
  e.stopPropagation();
};

export const onEnter = (callback?: (e: React.KeyboardEvent) => void) => (e: React.KeyboardEvent) => {
  if (e.key === KeyCodes.Enter) {
    callback?.(e);
  }
};

export const getFirstFocusableElement = (parent: HTMLElement = document.body): HTMLElement | undefined => {
  // Skip an element if either it OR any ancestor carries [data-no-autofocus].
  const focusables = parent.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  for (const el of focusables) {
    if (!el.closest('[data-no-autofocus]')) return el;
  }
  return undefined;
};

export const preventAutoCloseTriggerFocus = (event: Event) => {
  getFirstFocusableElement()?.focus();
  event.preventDefault();
};

export const afterAnimationFrame = (callback: Callable, framesToWait: number = 1) => {
  requestAnimationFrame(() => {
    if (framesToWait > 1) {
      afterAnimationFrame(callback, framesToWait - 1);
    } else {
      setTimeout(callback);
    }
  });
};

export function isTextSelected() {
  var selection = window.getSelection();
  return selection && selection.type === 'Range';
}

export function isTextBoxActive() {
  return document.activeElement?.matches('input,textarea');
}

export function clearWindowSelection() {
  const selection = window.getSelection();
  if (selection) {
    if (selection.empty) {
      // Chrome
      selection.empty();
    } else if (selection.removeAllRanges) {
      // Firefox
      selection.removeAllRanges();
    }
  }
}

export const closest = (element: HTMLElement | null, matcher: (element: HTMLElement | null) => boolean) => {
  let node = element;
  while (node && !matcher(node)) node = node?.parentElement;
  return node;
};

export function getDimensionWithoutPadding(element: HTMLElement) {
  const computedStyle = window.getComputedStyle(element);
  const height = element.clientHeight;
  const width = element.clientWidth;
  if (computedStyle.boxSizing === 'border-box') {
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);
    return {
      height: height - paddingBottom - paddingTop,
      width: width - paddingLeft - paddingRight
    };
  }
  return {
    height,
    width
  };
}

export function isElementInView(element: Element) {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.bottom <= window.innerHeight;
}

export function scrollIntoView(
  element: Element,
  options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center', inline: 'nearest' }
): Promise<void> {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          resolve();
        }
      });
    });

    observer.observe(element);
    element.scrollIntoView(options);
  });
}
